import React from 'react';
import { OnChangeStateTypeValues, TriggerActionsState } from '../constants/frontend-enums';
import { TriggerActionDataParams } from '../types/automation-rules.type';

export interface OnAddOptionHandlerProps {
    parentArrayName: TriggerActionsState;
}
interface OnDeleteOptionHandlerProps {
    parentArrayName: TriggerActionsState;
    id: number;
}

export interface AutomationRuleOption {
    id: number;
    content: React.ReactElement;
}

interface UseAutomationRuleOptionsStateProps {
    defaultOptions: AutomationRuleOption[];
    newOptionDefault: React.ReactElement;
    onAddDeleteGlobalState: (data: TriggerActionDataParams) => void;
}

export const useAutomationRuleOptionsState = (props: UseAutomationRuleOptionsStateProps) => {
    const { defaultOptions = [], newOptionDefault, onAddDeleteGlobalState } = props;

    const [options, setOptions] = React.useState<AutomationRuleOption[]>(defaultOptions);
    const totalOptionsRef = React.useRef(defaultOptions.length - 1);

    const deleteOption = (prev: AutomationRuleOption[], id: number, parentArrayName: TriggerActionsState) => {
        onAddDeleteGlobalState({
            type: OnChangeStateTypeValues.delete_array_value,
            index: id,
            parentArrayName,
        });
        return prev.filter((elem) => elem.id !== id);
    };

    const onDeleteOptionHandler = React.useCallback(
        ({ id, parentArrayName }: OnDeleteOptionHandlerProps) =>
            () =>
                setOptions((prev) => deleteOption(prev, id, parentArrayName)),
        []
    );

    const onAddOptionHandler = React.useCallback(
        ({ parentArrayName }: OnAddOptionHandlerProps) =>
            () => {
                setOptions((prev) => {
                    const newId = totalOptionsRef.current + 1;
                    totalOptionsRef.current = newId;
                    const clonedNewOptionDefault = React.cloneElement(newOptionDefault, {
                        id: newId,
                    });
                    onAddDeleteGlobalState({
                        type: OnChangeStateTypeValues.add_array_value,
                        index: newId,
                        parentArrayName,
                    });
                    return [...prev, { id: newId, content: clonedNewOptionDefault }];
                });
            },
        []
    );

    return { options, onDeleteOptionHandler, onAddOptionHandler };
};
